import { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Wallet, Send, QrCode, History, CreditCard, Banknote, Smartphone, 
  ArrowRightLeft, CheckCircle2, ShieldCheck, ArrowRight, User, LogOut,
  X, Copy, Clock, DollarSign, Phone, Mail, ChevronRight, AlertCircle,
  Loader2, Gift, Users, Trophy, Sparkles, UserPlus, BookUser, Trash2,
  LayoutDashboard, Activity, ArrowUpRight, ArrowDownLeft, Eye, Search,
  Shield, Lock, Scan, PlusCircle, Building2, Zap, Camera, Share2,
  Bell, Settings, HelpCircle, FileText, ChevronDown, Home, UserCircle,
  AlertTriangle, Download, Flag, Upload, BarChart3, Scale, ClipboardList,
  ChevronUp, Palette, Globe, ToggleLeft, ToggleRight, ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

// Velocity C.U. Sail Logo Icon
const BankLogoIcon = ({ size = 24 }) => (
  <img
    src="/velocity-logo.png"
    alt="Velocity C.U."
    width={size}
    height={size}
    style={{ objectFit: "contain", borderRadius: size > 30 ? 8 : 5 }}
  />
);

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  // Keep ref in sync with state so interceptor always has latest token
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Axios interceptor: attach Bearer token to every request when available
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      if (tokenRef.current) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (phone, pin) => {
    const res = await axios.post(`${API}/auth/login`, { phone, pin });
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* cookie cleared locally */ }
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) { /* non-critical refresh */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// SMS Notification Context (for demo simulation)
const NotificationContext = createContext(null);

const useNotification = () => useContext(NotificationContext);

const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);

  const showSMS = (data) => {
    setNotification(data);
    setTimeout(() => setNotification(null), 10000);
  };

  const hideSMS = () => setNotification(null);

  return (
    <NotificationContext.Provider value={{ notification, showSMS, hideSMS }}>
      {children}
      {notification && <SMSNotificationModal notification={notification} onClose={hideSMS} />}
    </NotificationContext.Provider>
  );
};

// SMS Notification Modal Component
const SMSNotificationModal = ({ notification, onClose }) => {
  const navigate = useNavigate();

  const handleClaimClick = () => {
    onClose();
    navigate(notification.claim_link);
  };

  return (
    <div className="sms-notification" data-testid="sms-notification">
      <div className="bg-[#19798a] px-4 py-2 flex items-center gap-2">
        <Smartphone className="w-4 h-4 text-white" />
        <span className="text-white text-sm font-medium">SMS Message</span>
        <button 
          onClick={onClose}
          className="ml-auto text-white/70 hover:text-white"
          data-testid="close-sms-notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4">
        <p className="text-slate-700 text-sm mb-3">{notification.message}</p>
        <div className="bg-slate-50 rounded-lg p-3 mb-3">
          <p className="text-xs text-slate-500 mb-1">Your OTP Code:</p>
          <p className="text-2xl font-bold text-[#19798a] mono">{notification.otp}</p>
        </div>
        {notification.claim_link && (
          <button
            onClick={handleClaimClick}
            className="w-full btn-gold text-sm"
            data-testid="claim-funds-btn"
          >
            Claim Your Money
          </button>
        )}
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#19798a]" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Header Component
const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="glass sticky top-0 z-40">
      <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <div 
          className="flex items-center gap-3 cursor-pointer" 
          onClick={() => navigate("/dashboard")}
          data-testid="header-logo"
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center">
            <BankLogoIcon size={40} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#19798a]">Velocity Pay</h1>
            <p className="text-[10px] text-slate-400">Powered by Velocity C.U.</p>
          </div>
        </div>
        
        {user && (
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-slate-700">{user.name}</p>
              <p className="text-xs text-slate-400">{user.phone}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-lg hover:bg-slate-100"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

// Login Page
const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoUsers, setDemoUsers] = useState([]);

  const fetchDemoUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/demo/users`);
      setDemoUsers(res.data.users);
    } catch (e) {
      // Non-critical: demo user list unavailable
    }
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
    fetchDemoUsers();
  }, [user, navigate, fetchDemoUsers]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      await login(phone, pin);
      navigate("/dashboard");
    } catch (e) {
      setError(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const selectDemoUser = (demoUser) => {
    setPhone(demoUser.phone);
    setPin("1234");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 gradient-hero flex-col justify-between p-12">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center">
              <BankLogoIcon size={48} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Velocity Pay</h1>
              <p className="text-xs text-white/50">Powered by Velocity C.U.</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
            Send Money to Anyone.<br />
            <span className="text-[#f48420]">No Bank Account Needed.</span>
          </h2>
          <p className="text-lg text-white/70 max-w-md">
            Instant P2P payments powered by FedNow. Send to friends, family, 
            or anyone with just their phone number.
          </p>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-white/60">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm">Bank-Grade Security</span>
          </div>
          <div className="flex items-center gap-2 text-white/60">
            <Clock className="w-5 h-5" />
            <span className="text-sm">Instant Transfers</span>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center">
              <BankLogoIcon size={40} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#19798a]">Velocity Pay</h1>
              <p className="text-[10px] text-slate-400">Powered by Velocity C.U.</p>
            </div>
          </div>
          
          <div className="card-surface p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Welcome Back</h2>
              <p className="text-slate-500 mt-1">Sign in to your wallet</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                    placeholder="+1 (___) ___-____"
                    data-testid="login-phone-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  PIN
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none mono text-center text-xl tracking-widest"
                  placeholder="••••"
                  maxLength={4}
                  data-testid="login-pin-input"
                />
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-brand flex items-center justify-center gap-2"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <button className="flex items-center justify-center gap-2 mx-auto text-[#19798a] hover:text-[#19798a]/80 transition-colors" data-testid="biometric-login-btn">
                <Scan className="w-5 h-5" />
                <span className="text-sm font-medium">Or use Face ID</span>
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-slate-500">New to Velocity Pay?{" "}
                <button onClick={() => navigate("/register")} className="text-[#19798a] font-semibold hover:underline" data-testid="signup-link">Sign Up</button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Registration + KYC Onboarding Page
const RegisterPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=phone+pin, 2=KYC-A, 3=KYC-B, 4=KYC-C, 5=KYC-D, 6=KYC-E
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [legalName, setLegalName] = useState("");
  const [dob, setDob] = useState("");
  const [idType, setIdType] = useState("");
  const [selfieReady, setSelfieReady] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreateAccount = () => {
    setError("");
    if (!phone || phone.length < 10) { setError("Please enter a valid phone number"); return; }
    if (pin.length < 4) { setError("PIN must be at least 4 digits"); return; }
    if (pin !== confirmPin) { setError("PINs do not match"); return; }
    setStep(2);
  };

  const handleKycA = () => {
    setError("");
    if (!legalName.trim()) { setError("Please enter your legal name"); return; }
    if (!dob) { setError("Please enter your date of birth"); return; }
    setStep(3);
  };

  const handleKycB = () => {
    if (!idType) return;
    setStep(4);
  };

  const handleKycC = () => {
    setSelfieReady(true);
    setStep(5);
    // Auto-advance through verification after ~12 seconds
    setTimeout(() => setStep(6), 12000);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { phone, pin, name: legalName, dob });
      await login(phone, pin);
      navigate("/dashboard");
    } catch (e) {
      setError(e.response?.data?.detail || "Registration failed");
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const stepIndicator = (currentStep) => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1,2,3,4,5].map(s => (
        <div key={s} className={`h-1.5 rounded-full transition-all ${s <= currentStep ? "w-8 bg-[#19798a]" : "w-4 bg-slate-200"}`} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center">
            <BankLogoIcon size={40} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#19798a]">Velocity Pay</h1>
            <p className="text-[10px] text-slate-400">Powered by Velocity C.U.</p>
          </div>
        </div>

        <div className="card-surface p-8">
          {/* Step 1: Phone + PIN */}
          {step === 1 && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900" data-testid="register-title">Create Your Wallet</h2>
                <p className="text-slate-500 mt-1">Enter your phone number and create a PIN</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                      placeholder="+1 (___) ___-____" data-testid="register-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Create PIN</label>
                  <input type="password" value={pin} onChange={(e) => setPin(e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none mono text-center text-xl tracking-widest"
                    placeholder="----" maxLength={4} data-testid="register-pin-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Confirm PIN</label>
                  <input type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none mono text-center text-xl tracking-widest"
                    placeholder="----" maxLength={4} data-testid="register-confirm-pin-input" />
                </div>
                {error && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}
                <button onClick={handleCreateAccount} className="w-full btn-brand flex items-center justify-center gap-2" data-testid="register-continue-btn">
                  Continue <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500">Already have an account?{" "}
                  <button onClick={() => navigate("/login")} className="text-[#19798a] font-semibold hover:underline" data-testid="login-link">Sign In</button>
                </p>
              </div>
            </>
          )}

          {/* KYC Screen A — Verify Your Identity */}
          {step === 2 && (
            <>
              {stepIndicator(1)}
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-7 h-7 text-[#19798a]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="kyc-a-title">Verify Your Identity</h2>
                <p className="text-slate-500 mt-2 text-sm">Federal regulations require us to verify your identity before activating your wallet.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Legal Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input type="text" value={legalName} onChange={(e) => setLegalName(e.target.value)}
                      className="w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                      placeholder="As shown on your government ID" data-testid="kyc-legal-name-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date of Birth</label>
                  <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                    data-testid="kyc-dob-input" />
                </div>
                {error && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}
                <button onClick={handleKycA} className="w-full btn-brand flex items-center justify-center gap-2" data-testid="kyc-a-continue-btn">
                  Continue <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {/* KYC Screen B — Upload Government ID */}
          {step === 3 && (
            <>
              {stepIndicator(2)}
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-[#19798a]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="kyc-b-title">Upload Government ID</h2>
                <p className="text-slate-500 mt-2 text-sm">Please upload a clear photo of your Driver's License or Passport</p>
              </div>
              <div className="space-y-4">
                <button
                  onClick={() => setIdType("license")}
                  className={`w-full p-5 rounded-xl border-2 text-left transition-all ${idType === "license" ? "border-[#19798a] bg-teal-50/50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="kyc-id-license-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <CreditCard className="w-6 h-6 text-[#19798a]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Driver's License</p>
                      <p className="text-xs text-slate-500 mt-0.5">Take Photo or Upload from Library</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setIdType("passport")}
                  className={`w-full p-5 rounded-xl border-2 text-left transition-all ${idType === "passport" ? "border-[#19798a] bg-teal-50/50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="kyc-id-passport-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <FileText className="w-6 h-6 text-[#19798a]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Passport</p>
                      <p className="text-xs text-slate-500 mt-0.5">Take Photo or Upload from Library</p>
                    </div>
                  </div>
                </button>
                <button onClick={handleKycB} disabled={!idType}
                  className={`w-full btn-brand flex items-center justify-center gap-2 ${!idType ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="kyc-b-continue-btn">
                  Continue <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-slate-400 text-center mt-5">Your ID is encrypted and stored securely. We do not share your information with third parties.</p>
            </>
          )}

          {/* KYC Screen C — Selfie Verification */}
          {step === 4 && (
            <>
              {stepIndicator(3)}
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-7 h-7 text-[#19798a]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="kyc-c-title">Selfie Verification</h2>
                <p className="text-slate-500 mt-2 text-sm">Take a selfie to match your government ID</p>
              </div>
              {/* Face guide oval */}
              <div className="flex justify-center mb-6">
                <div className="w-48 h-60 rounded-[50%] border-[3px] border-dashed border-[#19798a]/40 flex items-center justify-center bg-slate-50" data-testid="kyc-selfie-oval">
                  <div className="text-center">
                    <Camera className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 px-4">Position your face within the oval</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 text-center mb-6">Position your face within the oval and ensure good lighting</p>
              <button onClick={handleKycC} className="w-full btn-brand flex items-center justify-center gap-2" data-testid="kyc-c-take-selfie-btn">
                <Camera className="w-5 h-5" /> Take Selfie
              </button>
              <button onClick={handleKycC} className="w-full mt-3 text-sm text-[#19798a] hover:underline text-center" data-testid="kyc-c-upload-btn">
                Upload Photo Instead
              </button>
            </>
          )}

          {/* KYC Screen D — Verification In Progress */}
          {step === 5 && (
            <>
              {stepIndicator(4)}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900" data-testid="kyc-d-title">Verifying Your Identity</h2>
                <p className="text-slate-500 mt-2 text-sm">This usually takes 1-2 minutes</p>
              </div>
              {/* Spinner */}
              <div className="flex justify-center mb-8">
                <div className="w-20 h-20 rounded-full border-4 border-slate-200 border-t-[#19798a] animate-spin" data-testid="kyc-d-spinner" />
              </div>
              {/* Status steps */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700">Documents Received</span>
                </div>
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-amber-500 animate-spin flex-shrink-0" />
                  <span className="text-sm text-slate-700 animate-pulse">Identity Check In Progress</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded border-2 border-slate-300 flex-shrink-0" />
                  <span className="text-sm text-slate-400">Wallet Activation Pending</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 text-center">You'll receive an SMS confirmation when verified.</p>
              <p className="text-xs text-slate-400 text-center mt-2">This process is powered by bank-grade identity verification technology.</p>
            </>
          )}

          {/* KYC Screen E — Identity Verified */}
          {step === 6 && (
            <>
              {stepIndicator(5)}
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-testid="kyc-e-checkmark">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="kyc-e-title">Identity Verified!</h2>
                <p className="text-slate-500 text-sm mb-6">Welcome to Velocity Pay. Your wallet is now active and ready to use.</p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full mb-8" data-testid="kyc-verified-badge">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-semibold text-green-700">KYC Verified</span>
                </div>
                <div>
                  {error && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4"><AlertCircle className="w-4 h-4" />{error}</div>}
                  <button onClick={handleFinish} disabled={loading} className="w-full btn-brand flex items-center justify-center gap-2" data-testid="kyc-e-go-to-wallet-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Wallet className="w-5 h-5" /> Go to My Wallet</>}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ConsumerFooter = () => (
  <footer className="border-t border-slate-200 bg-white mt-8 py-4 px-4 text-center">
    <p className="text-xs text-slate-400">Velocity Pay is a service of Velocity C.U. NCUA Insured.</p>
  </footer>
);

const AdminFooter = () => (
  <footer className="border-t border-slate-200 bg-white py-4 px-6 text-center">
    <p className="text-xs text-slate-400">Powered by Velocity C.U. | Bank Management Platform | SOC 2 Compliant Infrastructure</p>
  </footer>
);

// Dashboard Page
const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wallet/transactions`);
      setTransactions(res.data.transactions);
    } catch (e) {
      // Non-critical: transaction list unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReferralStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referrals/stats`);
      setReferralStats(res.data);
    } catch (e) {
      // Non-critical: referral stats unavailable
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
    fetchReferralStats();
    refreshUser();
  }, [fetchTransactions, fetchReferralStats, refreshUser]);

  const quickActions = [
    { icon: Send, label: "Send Money", path: "/send", color: "text-[#19798a]", bg: "bg-teal-50" },
    { icon: PlusCircle, label: "Add Money", path: "/add-money", color: "text-emerald-600", bg: "bg-emerald-50" },
    { icon: QrCode, label: "QR Pay", path: "/qr-pay", color: "text-purple-600", bg: "bg-purple-50" },
    { icon: Banknote, label: "ATM Cash", path: "/atm", color: "text-green-600", bg: "bg-green-50" },
    { icon: Gift, label: "Rewards", path: "/referrals", color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <Header />
      
      <main className="container mx-auto px-4 md:px-8 py-8">
        {/* Balance Card */}
        <div className="gradient-hero rounded-2xl p-6 md:p-8 mb-6 text-white animate-fade-in" data-testid="balance-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-white/60 text-sm mb-1">Available Balance</p>
              <p className="balance-display text-4xl md:text-5xl">
                ${user?.wallet_balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <Lock className="w-4 h-4" />
            <span>{user?.bank_name || "eWallet"}</span>
          </div>
          <p className="text-white/40 text-xs mt-1">NCUA Insured via Velocity C.U.</p>
        </div>
        
        {/* Referral Rewards Banner */}
        <div 
          className="gradient-gold rounded-xl p-4 mb-6 cursor-pointer hover:shadow-gold"
          onClick={() => navigate("/referrals")}
          data-testid="referral-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white">Invite Friends, Earn $5!</p>
                <p className="text-sm text-white/70">
                  {referralStats?.total_referrals > 0 
                    ? `You've earned $${referralStats?.total_earned?.toFixed(2)} from ${referralStats?.total_referrals} referrals`
                    : "Send money to new users and both of you earn rewards"}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white" />
          </div>
        </div>
        
        {/* Quick Actions Grid */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-8">
          {quickActions.map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="quick-action"
              data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className={`w-12 h-12 ${action.bg} rounded-xl flex items-center justify-center mb-3`}>
                <action.icon className={`w-6 h-6 ${action.color}`} />
              </div>
              <span className="text-sm font-medium text-slate-700">{action.label}</span>
            </button>
          ))}
        </div>
        
        {/* Recent Transactions */}
        <div className="card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Recent Activity</h2>
            <button 
              onClick={() => navigate("/history")}
              className="text-sm text-[#19798a] hover:underline flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400">No transactions yet</p>
              <button 
                onClick={() => navigate("/send")}
                className="mt-4 text-[#19798a] font-medium hover:underline"
              >
                Send your first payment
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {transactions.slice(0, 5).map((tx) => (
                <TransactionItem key={tx.id} transaction={tx} onClick={setSelectedTx} />
              ))}
            </div>
          )}
        </div>
      </main>
      {selectedTx && <TransactionReceipt transaction={selectedTx} onClose={() => setSelectedTx(null)} />}
      <ConsumerFooter />
      <BottomNav active="home" />
    </div>
  );
};

// Transaction Item Component
const TransactionItem = ({ transaction, onClick }) => {
  const isSent = transaction.type === "sent";
  
  const getIcon = () => {
    switch (transaction.transaction_type) {
      case "atm_withdrawal": return Banknote;
      case "qr_payment": return QrCode;
      default: return isSent ? Send : ArrowRightLeft;
    }
  };
  
  const Icon = getIcon();
  
  return (
    <div className="transaction-item cursor-pointer hover:bg-slate-50" data-testid={`transaction-${transaction.id}`} onClick={() => onClick && onClick(transaction)}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isSent ? "bg-red-50" : "bg-green-50"
        }`}>
          <Icon className={`w-5 h-5 ${isSent ? "text-red-500" : "text-green-500"}`} />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">{transaction.counterparty_name}</p>
          <p className="text-xs text-slate-400">
            {transaction.memo || transaction.transaction_type.replace("_", " ")}
          </p>
        </div>
      </div>
      <div className="text-right flex items-center gap-2">
        <div>
          <p className={`text-sm font-semibold ${isSent ? "text-red-600" : "text-green-600"}`}>
            {isSent ? "-" : "+"}${transaction.amount.toFixed(2)}
          </p>
          <p className="text-xs text-slate-400">
            {new Date(transaction.created_at).toLocaleDateString()}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300" />
      </div>
    </div>
  );
};

// Send Money Page
const SendMoneyPage = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const { showSMS } = useNotification();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactName, setContactName] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);

  const [contactError, setContactError] = useState("");

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contacts`);
      setContacts(res.data.contacts || []);
    } catch (e) {
      setContactError("Could not load contacts");
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const isAlreadyContact = (phoneNum) => {
    return contacts.some(c => c.phone === phoneNum);
  };

  const handleSaveContact = async () => {
    if (!contactName.trim()) return;
    setSavingContact(true);
    try {
      await axios.post(`${API}/contacts`, { name: contactName.trim(), phone });
      setContactSaved(true);
      fetchContacts();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save contact");
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      fetchContacts();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete contact");
    }
  };

  const checkRecipient = async () => {
    if (!phone) {
      setError("Please enter a phone number");
      return;
    }
    setLoading(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/send/check-recipient`, { phone });
      setRecipient(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to check recipient");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    setLoading(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/send/money`, {
        recipient_phone: phone,
        amount: parseFloat(amount),
        memo
      });
      
      setResult(res.data);
      setStep(4);
      setContactSaved(false);
      setContactName("");
      refreshUser();
      
      // Show SMS notification if new user
      if (res.data.requires_onboarding && res.data.sms_notification) {
        showSMS(res.data.sms_notification);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send money");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-lg">
        <button 
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Dashboard
        </button>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? "bg-[#19798a] text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-1 rounded ${step > s ? "bg-[#19798a]" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>
        
        <div className="card-surface p-6 animate-slide-up">
          {/* Step 1: Enter Phone */}
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Send Money</h2>
              <p className="text-slate-500 mb-6">Enter recipient's phone number</p>
              
              {/* Saved Contacts */}
              {contacts.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <BookUser className="w-4 h-4 inline mr-1" />
                    Saved Contacts
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {contacts.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => setPhone(c.phone)}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                          phone === c.phone 
                            ? "border-[#19798a] bg-teal-50" 
                            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                        data-testid={`contact-${c.phone}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#19798a] rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">{c.name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{c.name}</p>
                            <p className="text-xs text-slate-500">{c.phone}</p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteContact(c.id, e)}
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-contact-${c.phone}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="my-4 flex items-center gap-3">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400">or enter number</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                    placeholder="+1 555 123 4567"
                    data-testid="send-phone-input"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  The recipient will receive funds via SMS if they don't have a wallet
                </p>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <button
                onClick={checkRecipient}
                disabled={loading}
                className="w-full btn-brand flex items-center justify-center gap-2"
                data-testid="send-continue-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Continue <ArrowRight className="w-5 h-5" /></>
                )}
              </button>
            </>
          )}
          
          {/* Step 2: Enter Amount */}
          {step === 2 && (
            <>
              <div className="mb-6">
                {recipient?.exists ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">{recipient.name}</p>
                      <p className="text-xs text-green-600">Existing wallet user</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-800">New User</p>
                      <p className="text-xs text-amber-600">Will receive SMS to claim funds</p>
                    </div>
                  </div>
                )}
              </div>
              
              <h2 className="text-xl font-bold text-slate-900 mb-2">Enter Amount</h2>
              <p className="text-slate-500 mb-6">How much would you like to send?</p>
              
              <div className="mb-6">
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full h-16 pl-12 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none text-3xl font-bold"
                    placeholder="0.00"
                    data-testid="send-amount-input"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Memo (Optional)
                </label>
                <input
                  type="text"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                  placeholder="What's this for?"
                  data-testid="send-memo-input"
                />
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 px-4 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!amount || parseFloat(amount) <= 0}
                  className="flex-1 btn-brand"
                  data-testid="send-review-btn"
                >
                  Review
                </button>
              </div>
            </>
          )}
          
          {/* Step 3: Review & Confirm */}
          {step === 3 && (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-6">Confirm Transfer</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">To</span>
                  <span className="font-medium text-slate-900">{phone}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-slate-500">Amount</span>
                  <span className="text-2xl font-bold text-slate-900">${parseFloat(amount).toFixed(2)}</span>
                </div>
                {memo && (
                  <div className="flex justify-between items-center py-3 border-b border-slate-100">
                    <span className="text-slate-500">Memo</span>
                    <span className="text-slate-700">{memo}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-500">Via</span>
                  <span className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    FedNow Instant
                  </span>
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 px-4 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSend}
                  disabled={loading}
                  className="flex-1 btn-gold animate-pulse-gold"
                  data-testid="send-confirm-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Confirm & Send"}
                </button>
              </div>
            </>
          )}
          
          {/* Step 4: Success */}
          {step === 4 && result && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Money Sent!</h2>
              <p className="text-slate-500 mb-6">{result.message}</p>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-500">FedNow Reference</span>
                  <span className="mono text-sm text-slate-700">{result.fednow_reference}</span>
                </div>
                {result.requires_onboarding && (
                  <p className="text-sm text-amber-600 mt-2">
                    Recipient will receive SMS to claim funds
                  </p>
                )}
              </div>

              {/* Save to Contacts prompt - only for new numbers not already saved */}
              {!isAlreadyContact(phone) && !contactSaved && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 text-left" data-testid="save-contact-prompt">
                  <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="w-5 h-5 text-[#19798a]" />
                    <p className="font-medium text-[#19798a]">Save to Contacts?</p>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">Save <span className="font-medium">{phone}</span> for faster transfers next time.</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="Enter name"
                      className="flex-1 h-10 px-3 rounded-lg border border-slate-200 bg-white focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none text-sm"
                      data-testid="save-contact-name-input"
                    />
                    <button
                      onClick={handleSaveContact}
                      disabled={savingContact || !contactName.trim()}
                      className="px-4 h-10 bg-[#19798a] text-white text-sm font-medium rounded-lg hover:bg-[#106070] disabled:opacity-50 transition-colors"
                      data-testid="save-contact-btn"
                    >
                      {savingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                    </button>
                  </div>
                </div>
              )}

              {contactSaved && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 mb-6" data-testid="contact-saved-msg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="text-sm text-green-700 font-medium">Contact saved!</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 py-3 px-4 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                  data-testid="send-done-btn"
                >
                  Done
                </button>
                <button
                  onClick={() => { setStep(1); setPhone(""); setAmount(""); setMemo(""); setResult(null); setContactSaved(false); setContactName(""); }}
                  className="flex-1 btn-brand"
                >
                  Send Another
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <ConsumerFooter />
    </div>
  );
};

// Claim Funds Page
const ClaimPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [claimInfo, setClaimInfo] = useState(null);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const fetchClaimInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/claim/${token}`);
      setClaimInfo(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid or expired claim link");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchClaimInfo();
  }, [fetchClaimInfo]);

  const handleClaim = async () => {
    if (!phone || !otp) {
      setError("Please enter phone and OTP");
      return;
    }
    setVerifying(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/claim/verify`, {
        phone,
        otp,
        claim_token: token
      });
      
      setSuccess(true);
      
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 2000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to verify");
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#19798a]" />
      </div>
    );
  }

  if (error && !claimInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="card-surface p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Link Invalid</h2>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="card-surface p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome!</h2>
          <p className="text-slate-500">Your wallet has been created. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="gradient-hero py-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="flex items-center gap-3 justify-center mb-6">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center">
              <BankLogoIcon size={48} />
            </div>
            <div className="text-left">
              <h1 className="text-xl font-bold text-white">Velocity Pay</h1>
              <p className="text-xs text-white/50">Powered by Velocity C.U.</p>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-2">
            {claimInfo?.sender_name} sent you
          </h2>
          <p className="balance-display text-5xl text-[#f48420] mb-4">
            ${claimInfo?.amount?.toFixed(2)}
          </p>
          <p className="text-white/70">Claim your money now. No bank account needed.</p>
        </div>
      </div>
      
      {/* Claim Form */}
      <div className="max-w-md mx-auto px-4 -mt-6">
        <div className="card-surface p-6 animate-slide-up" data-testid="claim-form">
          <div className="flex items-center gap-2 justify-center mb-6">
            <ShieldCheck className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-600 font-medium">Secure & Verified</span>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Your Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                placeholder={`+1 *** *** ${claimInfo?.phone_hint || "****"}`}
                data-testid="claim-phone-input"
              />
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Enter OTP from SMS
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full h-14 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none mono text-center text-2xl tracking-widest"
              placeholder="••••••"
              maxLength={6}
              data-testid="claim-otp-input"
            />
            {claimInfo?.otp_for_demo && (
              <p className="text-xs text-amber-600 mt-2 text-center">
                Demo OTP: <span className="mono font-bold">{claimInfo.otp_for_demo}</span>
              </p>
            )}
          </div>
          
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <button
            onClick={handleClaim}
            disabled={verifying}
            className="w-full btn-gold animate-pulse-gold"
            data-testid="claim-submit-btn"
          >
            {verifying ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Claim My Money"}
          </button>
          
          <p className="text-xs text-slate-400 text-center mt-4">
            By claiming, you agree to create a Velocity Pay wallet
          </p>
        </div>
      </div>
    </div>
  );
};

// ATM Withdrawal Page
const ATMPage = () => {
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [withdrawalInfo, setWithdrawalInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const initiateWithdrawal = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    setLoading(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/atm/initiate`, {
        amount: parseFloat(amount)
      });
      setWithdrawalInfo(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to initiate withdrawal");
    } finally {
      setLoading(false);
    }
  };

  const simulateATM = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/atm/verify`, {
        phone: user.phone,
        otp: withdrawalInfo.otp,
        reference_code: withdrawalInfo.reference_code
      });
      setStep(3);
      refreshUser();
    } catch (e) {
      setError(e.response?.data?.detail || "Withdrawal failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-lg">
        <button 
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Dashboard
        </button>
        
        <div className="card-surface p-6 animate-slide-up">
          {step === 1 && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Cardless ATM</h2>
                  <p className="text-sm text-slate-500">Withdraw cash without a card</p>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Withdrawal Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full h-16 pl-12 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none text-3xl font-bold"
                    placeholder="0.00"
                    data-testid="atm-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Max: ${user?.wallet_balance?.toFixed(2)}
                </p>
              </div>
              
              <div className="grid grid-cols-4 gap-2 mb-6">
                {[20, 40, 60, 100].map((val) => (
                  <button
                    key={val}
                    onClick={() => setAmount(val.toString())}
                    className="py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium"
                  >
                    ${val}
                  </button>
                ))}
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <button
                onClick={initiateWithdrawal}
                disabled={loading}
                className="w-full btn-brand flex items-center justify-center gap-2"
                data-testid="atm-initiate-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Get Withdrawal Code <ArrowRight className="w-5 h-5" /></>
                )}
              </button>
            </>
          )}
          
          {step === 2 && withdrawalInfo && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Banknote className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Withdrawal Ready</h2>
                <p className="text-slate-500">Use these details at any participating ATM</p>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">Phone Number</p>
                  <p className="text-lg font-bold text-slate-900 mono">{user?.phone}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">OTP Code</p>
                  <p className="text-3xl font-bold text-[#19798a] mono">{withdrawalInfo.otp}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-1">Reference Code</p>
                  <p className="text-2xl font-bold text-slate-900 mono">{withdrawalInfo.reference_code}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700">Expires in {withdrawalInfo.expires_in_minutes} minutes</span>
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" data-testid="atm-instructions">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <p className="font-medium text-green-800">Ready for Withdrawal</p>
                </div>
                <p className="text-sm text-green-700">Present your phone number and OTP code at any participating ATM.</p>
              </div>
              
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full btn-brand"
                data-testid="atm-done-btn"
              >
                Back to Dashboard
              </button>
            </>
          )}
          
          {step === 3 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Withdrawal Complete!</h2>
              <p className="text-slate-500 mb-6">
                ${parseFloat(amount).toFixed(2)} has been withdrawn
              </p>
              
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full btn-brand"
                data-testid="atm-done-btn"
              >
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </main>
      <ConsumerFooter />
    </div>
  );
};

// QR Pay Page
const QRPayPage = () => {
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [merchantName, setMerchantName] = useState("");
  const [qrInfo, setQrInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generateQR = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    setLoading(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/qr/generate`, {
        amount: parseFloat(amount),
        merchant_name: merchantName || "Merchant"
      });
      setQrInfo(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate QR");
    } finally {
      setLoading(false);
    }
  };

  const completePayment = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/qr/complete`, {
        qr_token: qrInfo.qr_token,
        payer_id: user.id
      });
      setStep(3);
      refreshUser();
    } catch (e) {
      setError(e.response?.data?.detail || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-lg">
        <button 
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Dashboard
        </button>
        
        <div className="card-surface p-6 animate-slide-up">
          {step === 1 && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">QR Pay</h2>
                  <p className="text-sm text-slate-500">Pay merchants instantly</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Merchant Name (Optional)
                </label>
                <input
                  type="text"
                  value={merchantName}
                  onChange={(e) => setMerchantName(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none"
                  placeholder="Coffee Shop"
                  data-testid="qr-merchant-input"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Payment Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full h-16 pl-12 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none text-3xl font-bold"
                    placeholder="0.00"
                    data-testid="qr-amount-input"
                  />
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <button
                onClick={generateQR}
                disabled={loading}
                className="w-full btn-brand flex items-center justify-center gap-2"
                data-testid="qr-generate-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>Generate QR Code <ArrowRight className="w-5 h-5" /></>
                )}
              </button>
            </>
          )}
          
          {step === 2 && qrInfo && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Scan to Pay</h2>
                <p className="text-slate-500">Show this QR to the merchant</p>
              </div>
              
              <div className="bg-white border-4 border-[#19798a] rounded-2xl p-6 mb-6">
                <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 grid grid-cols-8 gap-1 p-4">
                    {Array.from({ length: 64 }).map((_, i) => (
                      <div 
                        key={`qr-cell-${i}`} 
                        className={`rounded-sm ${Math.random() > 0.5 ? "bg-[#19798a]" : "bg-white"}`}
                      />
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center shadow-lg">
                      <BankLogoIcon size={64} />
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm text-slate-500">{qrInfo.merchant_name}</p>
                  <p className="text-2xl font-bold text-slate-900">${qrInfo.amount.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-3 flex items-center gap-2 mb-6">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-700">Expires in {qrInfo.expires_in_minutes} minutes</span>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="flex items-center justify-center gap-3 py-4 mb-4" data-testid="qr-awaiting-scan">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-green-700">Awaiting Scan...</span>
              </div>
              
              <button
                onClick={() => { setStep(1); setQrInfo(null); }}
                className="w-full py-3 px-4 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
            </>
          )}
          
          {step === 3 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Complete!</h2>
              <p className="text-slate-500 mb-6">
                ${parseFloat(amount).toFixed(2)} paid to {merchantName || "Merchant"}
              </p>
              
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full btn-brand"
                data-testid="qr-done-btn"
              >
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </main>
      <ConsumerFooter />
    </div>
  );
};

// Transaction History Page
const HistoryPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wallet/transactions`);
      setTransactions(res.data.transactions);
    } catch (e) {
      // Non-critical: transaction list unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-2xl">
        <button 
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Dashboard
        </button>
        
        <div className="card-surface p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Transaction History</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((tx) => (
                <TransactionItem key={tx.id} transaction={tx} />
              ))}
            </div>
          )}
        </div>
      </main>
      <ConsumerFooter />
    </div>
  );
};

// Referrals Page
const ReferralsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referrals/stats`);
      setStats(res.data);
    } catch (e) {
      // Non-critical: referral stats unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-lg">
        <button 
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Dashboard
        </button>
        
        {/* Rewards Header */}
        <div className="gradient-gold rounded-2xl p-6 mb-6 text-center" data-testid="referral-hero">
          <div className="w-16 h-16 bg-white/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-[#19798a]" />
          </div>
          <h1 className="text-2xl font-bold text-[#19798a] mb-2">Referral Rewards</h1>
          <p className="text-[#19798a]/70">Invite friends and earn together!</p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="card-surface p-4 text-center">
            <p className="text-3xl font-bold text-[#19798a]">{stats?.total_referrals || 0}</p>
            <p className="text-sm text-slate-500">Friends Invited</p>
          </div>
          <div className="card-surface p-4 text-center">
            <p className="text-3xl font-bold text-green-600">${stats?.total_earned?.toFixed(2) || "0.00"}</p>
            <p className="text-sm text-slate-500">Total Earned</p>
          </div>
        </div>
        
        {/* How It Works */}
        <div className="card-surface p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            How It Works
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-[#19798a]">1</span>
              </div>
              <div>
                <p className="font-medium text-slate-800">Send money to someone new</p>
                <p className="text-sm text-slate-500">Use Send Money to pay a friend who doesn't have an account yet</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-green-600">2</span>
              </div>
              <div>
                <p className="font-medium text-slate-800">They claim their funds</p>
                <p className="text-sm text-slate-500">Your friend verifies their phone and creates their wallet</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-amber-600">3</span>
              </div>
              <div>
                <p className="font-medium text-slate-800">You both earn rewards!</p>
                <p className="text-sm text-slate-500">You get <span className="font-bold text-green-600">$5</span> and they get <span className="font-bold text-green-600">$10</span> welcome bonus</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Reward Tiers */}
        <div className="card-surface p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Reward Tiers</h2>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-medium">You Earn (Per Referral)</span>
              </div>
              <span className="text-lg font-bold text-green-600">$5.00</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium">Friend Gets (Welcome Bonus)</span>
              </div>
              <span className="text-lg font-bold text-green-600">$10.00</span>
            </div>
          </div>
        </div>
        
        {/* CTA Button */}
        <button
          onClick={() => navigate("/send")}
          className="w-full btn-gold flex items-center justify-center gap-2"
          data-testid="invite-friends-btn"
        >
          <Send className="w-5 h-5" />
          Send Money & Earn Rewards
        </button>
        
        {/* Recent Referrals */}
        {stats?.recent_referrals?.length > 0 && (
          <div className="card-surface p-6 mt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Recent Referrals</h2>
            <div className="space-y-3">
              {stats.recent_referrals.map((ref) => (
                <div key={ref.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">New user joined</p>
                      <p className="text-xs text-slate-400">{ref.referee_phone.slice(-4)}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-green-600">+${ref.referrer_bonus}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <ConsumerFooter />
    </div>
  );
};

// Mobile Simulator Page
const MobileSimulatorPage = () => {
  const [searchParams] = useSearchParams();
  const demoPath = searchParams.get('path') || '/login';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-8">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Info Panel */}
        <div className="text-white order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center">
              <BankLogoIcon size={48} />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Velocity Pay</h1>
              <p className="text-sm text-white/50">Powered by Velocity C.U.</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-bold mb-4">
            Experience the Future of
            <span className="text-[#f48420]"> Mobile Payments</span>
          </h2>
          
          <p className="text-lg text-white/70 mb-8">
            See how our eWallet works on a mobile device. Send money, withdraw cash from ATMs, 
            and pay merchants - all without a bank account.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/80">
              <CheckCircle2 className="w-5 h-5 text-[#f48420]" />
              <span>Instant P2P transfers via FedNow</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <CheckCircle2 className="w-5 h-5 text-[#f48420]" />
              <span>SMS onboarding - no bank account needed</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <CheckCircle2 className="w-5 h-5 text-[#f48420]" />
              <span>Cardless ATM withdrawals</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <CheckCircle2 className="w-5 h-5 text-[#f48420]" />
              <span>QR code payments at merchants</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <CheckCircle2 className="w-5 h-5 text-[#f48420]" />
              <span>Referral rewards - earn $5 per invite!</span>
            </div>
          </div>
          
          <div className="mt-8 flex gap-4">
            <a 
              href="/login"
              className="btn-brand px-6"
            >
              Open Full App
            </a>
            <a 
              href="/mobile?path=/dashboard"
              className="px-6 py-3 rounded-lg border border-white/20 text-white hover:bg-white/10"
            >
              View Dashboard
            </a>
          </div>
        </div>
        
        {/* Phone Mockup */}
        <div className="order-1 lg:order-2 flex justify-center">
          <div className="relative">
            <div className="w-[320px] h-[660px] bg-black rounded-[3rem] p-3 shadow-2xl shadow-black/50">
              <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-20"></div>
                <iframe
                  src={demoPath}
                  className="w-full h-full border-0 bg-white"
                  title="Mobile Preview"
                  style={{ borderRadius: '2rem' }}
                />
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent rounded-[3rem] pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== ADMIN PANEL =====================

const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already authenticated via cookie
    axios.get(`${API}/admin/stats`).then(() => navigate("/admin")).catch(() => {});
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { email, password });
      navigate("/admin");
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #19798a 0%, #091E52 100%)" }}>
      <div className="w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
            <BankLogoIcon size={56} />
          </div>
          <h1 className="text-2xl font-bold text-white">Velocity Pay</h1>
          <p className="text-white/50 text-sm">Admin Portal</p>
        </div>
        <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-form">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Admin Sign In</h2>
          <p className="text-slate-500 text-sm mb-6">Access the bank management dashboard</p>
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4" data-testid="admin-login-error">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 pl-11 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none" placeholder="admin@velocity.com" data-testid="admin-email-input" />
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none" placeholder="Enter password" data-testid="admin-password-input" />
          </div>
          <button type="submit" disabled={loading} className="w-full btn-brand flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Sign In <ArrowRight className="w-5 h-5" /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [disputeDetail, setDisputeDetail] = useState(null);
  const [complianceFilter, setComplianceFilter] = useState("all");
  const [disputeFilter, setDisputeFilter] = useState("all");
  const [auditRange, setAuditRange] = useState("90");
  const [reportRange, setReportRange] = useState("30");
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, txRes, refRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/transactions`),
        axios.get(`${API}/admin/referrals`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTransactions(txRes.data.transactions);
      setReferrals(refRes.data.referrals);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`); } catch (e) { /* cookie cleared locally */ }
    navigate("/admin/login");
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
  };

  const sortData = (data, key, dir) => {
    if (!key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[key] ?? "";
      const bVal = b[key] ?? "";
      if (typeof aVal === "number") return dir === "asc" ? aVal - bVal : bVal - aVal;
      return dir === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
  };

  const SortHeader = ({ label, sortKey, className = "" }) => (
    <th className={`text-xs font-medium text-slate-500 uppercase px-4 py-3 cursor-pointer hover:text-slate-700 select-none ${className}`} onClick={() => handleSort(sortKey)}>
      <div className={`flex items-center gap-1 ${className.includes("text-right") ? "justify-end" : ""}`}>
        {label}
        {sortConfig.key === sortKey ? (sortConfig.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 opacity-30" />}
      </div>
    </th>
  );

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.phone?.includes(searchTerm)
  );

  const filteredTx = transactions.filter(tx =>
    tx.sender_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.sender_phone?.includes(searchTerm) ||
    tx.recipient_phone?.includes(searchTerm) ||
    tx.fednow_reference?.includes(searchTerm)
  );

  // === COMPLIANCE DATA ===
  const kycData = [
    { name: "Sarah Mitchell", phone: "+15551234567", status: "Verified", idType: "Driver's License", submitted: "3/15/2026", reviewed: "3/15/2026" },
    { name: "James Carter", phone: "+15559871234", status: "Verified", idType: "Passport", submitted: "3/10/2026", reviewed: "3/11/2026" },
    { name: "Maria Gonzalez", phone: "+15554445678", status: "Pending", idType: "Driver's License", submitted: "4/1/2026", reviewed: null },
    { name: "Robert Thompson", phone: "+15552223333", status: "Failed", idType: "Passport", submitted: "3/28/2026", reviewed: "3/29/2026" },
    { name: "Aisha Williams", phone: "+15556667777", status: "Verified", idType: "Driver's License", submitted: "3/5/2026", reviewed: "3/5/2026" },
  ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredKyc = useMemo(() => complianceFilter === "all" ? kycData : kycData.filter(k => k.status.toLowerCase() === complianceFilter.toLowerCase()), [complianceFilter]);

  const flaggedTx = [
    { txId: "TXN-4892", user: "Sarah Mitchell", amount: 3200, type: "P2P Send", date: "3/31/2026", flag: "Review Required", flagColor: "yellow" },
    { txId: "TXN-5103", user: "James Carter", amount: 12500, type: "ACH Funding", date: "4/1/2026", flag: "CTR Required", flagColor: "red" },
    { txId: "TXN-4901", user: "Maria Gonzalez", amount: 3750, type: "P2P Send", date: "3/30/2026", flag: "Review Required", flagColor: "yellow" },
  ];

  const ofacData = [
    { user: "Sarah Mitchell", status: "Cleared", date: "4/1/2026" },
    { user: "James Carter", status: "Cleared", date: "4/1/2026" },
    { user: "Maria Gonzalez", status: "Pending", date: "4/1/2026" },
    { user: "Robert Thompson", status: "Cleared", date: "3/29/2026" },
    { user: "Aisha Williams", status: "Cleared", date: "4/1/2026" },
  ];

  // === DISPUTES DATA ===
  const disputes = [
    { id: "DSP-001", user: "Sarah Mitchell", phone: "+15551234567", txId: "TXN-3821", amount: 120, type: "Unauthorized", dateFiled: "3/28/2026", status: "Open", kycStatus: "Verified", txDate: "3/27/2026", txType: "P2P Send", memo: "Payment to unknown", rail: "FedNow Instant", statement: "User reports transaction was not authorized" },
    { id: "DSP-002", user: "James Carter", phone: "+15559871234", txId: "TXN-4102", amount: 250, type: "Error", dateFiled: "3/30/2026", status: "Under Review", kycStatus: "Verified", txDate: "3/29/2026", txType: "QR Payment", memo: "Duplicate charge", rail: "Internal", statement: "User reports being charged twice for the same purchase" },
    { id: "DSP-003", user: "Aisha Williams", phone: "+15556667777", txId: "TXN-3654", amount: 40, type: "Fraud", dateFiled: "3/15/2026", status: "Resolved", kycStatus: "Verified", txDate: "3/14/2026", txType: "QR Payment", memo: "Suspicious merchant", rail: "Internal", statement: "User reports fraudulent merchant charge" },
  ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredDisputes = useMemo(() => disputeFilter === "all" ? disputes : disputes.filter(d => d.status.toLowerCase().replace(" ", "-") === disputeFilter), [disputeFilter]);

  // === AUDIT LOG DATA ===
  const auditLog = [
    { ts: "4/2/2026 5:23 AM", admin: "admin@velocity.com", action: "Login", details: "Successful login", ip: "192.168.1.45", status: "Success" },
    { ts: "4/2/2026 5:25 AM", admin: "admin@velocity.com", action: "Transaction Viewed", details: "TXN-5103 reviewed", ip: "192.168.1.45", status: "Success" },
    { ts: "4/2/2026 5:26 AM", admin: "admin@velocity.com", action: "Transaction Flagged", details: "TXN-5103 flagged CTR Required", ip: "192.168.1.45", status: "Success" },
    { ts: "4/1/2026 11:45 PM", admin: "admin@velocity.com", action: "Dispute Reviewed", details: "DSP-002 status updated to Under Review", ip: "192.168.1.12", status: "Success" },
    { ts: "4/1/2026 3:12 PM", admin: "admin@velocity.com", action: "User Viewed", details: "Sarah Mitchell profile accessed", ip: "192.168.1.12", status: "Success" },
    { ts: "3/31/2026 9:05 AM", admin: "admin@velocity.com", action: "Settings Changed", details: "Daily send limit updated to $2,500", ip: "192.168.1.45", status: "Success" },
    { ts: "3/30/2026 2:34 PM", admin: "admin@velocity.com", action: "Dispute Resolved", details: "DSP-003 resolved in user's favor", ip: "192.168.1.12", status: "Success" },
    { ts: "3/29/2026 10:15 AM", admin: "admin@velocity.com", action: "KYC Reviewed", details: "Robert Thompson — KYC marked Failed", ip: "192.168.1.45", status: "Success" },
    { ts: "3/28/2026 8:00 AM", admin: "admin@velocity.com", action: "Login", details: "Failed login attempt", ip: "203.45.67.89", status: "Failed" },
    { ts: "3/28/2026 8:01 AM", admin: "admin@velocity.com", action: "Login", details: "Successful login after failed attempt", ip: "192.168.1.45", status: "Success" },
  ];

  // === SETTINGS STATE ===
  const [settingsLimits, setSettingsLimits] = useState({ dailySend: "2500", singleMax: "1000", dailyATM: "500", monthlyFunding: "10000" });
  const [settingsReferral, setSettingsReferral] = useState({ active: true, referrerBonus: "5.00", newUserBonus: "10.00", maxPerMonth: "10" });
  const [settingsBranding, setSettingsBranding] = useState({ bankName: "Velocity C.U.", subtitle: "Powered by Velocity C.U.", primaryColor: "#19798a" });
  const [settingsNotifs, setSettingsNotifs] = useState({ newUser: true, txOver1k: true, failedKyc: true, newDispute: true, ctrReached: true, ofacMatch: true, newDevice: true });

  // KYC lookup map for users table
  const kycMap = { "+15551234567": "Verified", "+15559871234": "Verified", "+15554445678": "Pending", "+15552223333": "Failed", "+15556667777": "Verified" };

  // Dispute lookup map for transactions table
  const disputeTxIds = new Set(disputes.map(d => d.txId));

  const statusBadge = (status) => {
    const colors = {
      verified: "bg-green-50 text-green-700", cleared: "bg-green-50 text-green-700", resolved: "bg-green-50 text-green-700", success: "bg-green-50 text-green-700",
      pending: "bg-amber-50 text-amber-700", review: "bg-amber-50 text-amber-700", "in progress": "bg-amber-50 text-amber-700", "under review": "bg-amber-50 text-amber-700", "review required": "bg-amber-50 text-amber-700",
      failed: "bg-red-50 text-red-700", flagged: "bg-red-50 text-red-700", "ctr required": "bg-red-50 text-red-700", open: "bg-red-50 text-red-700", "match detected": "bg-red-50 text-red-700",
      denied: "bg-slate-100 text-slate-500", inactive: "bg-slate-100 text-slate-500",
      normal: "bg-green-50 text-green-700",
    };
    const c = colors[status.toLowerCase()] || "bg-slate-100 text-slate-500";
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c}`}>{status}</span>;
  };

  const getFlagStatus = (amount) => {
    if (amount >= 10000) return "CTR Required";
    if (amount >= 3000) return "Review";
    return "Normal";
  };

  const exportCSV = () => {
    const rows = [["Timestamp","Admin User","Action","Details","IP Address","Status"]];
    auditLog.forEach(l => rows.push([l.ts, l.admin, l.action, l.details, l.ip, l.status]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "audit_log.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    window.print();
  };

  const ToggleSwitch = ({ checked, onChange, label, sublabel }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-400">{checked ? sublabel || "SMS + Email" : "Off"}</p>
      </div>
      <button onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-[#19798a]" : "bg-slate-300"}`} data-testid={`toggle-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#19798a]" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "users", label: "Users", icon: Users },
    { id: "transactions", label: "Transactions", icon: Activity },
    { id: "referrals", label: "Referrals", icon: Gift },
    { id: "compliance", label: "Compliance", icon: Shield },
    { id: "disputes", label: "Disputes", icon: Scale },
    { id: "audit", label: "Audit Log", icon: ClipboardList },
    { id: "settings", label: "Settings", icon: Settings },
    { id: "reports", label: "Reports", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Header */}
      <header className="bg-[#19798a] text-white px-6 py-4 flex items-center justify-between" data-testid="admin-header">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center">
            <BankLogoIcon size={36} />
          </div>
          <div>
            <h1 className="text-lg font-bold">Velocity Pay</h1>
            <p className="text-[10px] text-white/50">Admin Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-white/70">admin@velocity.com</span>
          <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-white/70 hover:text-white" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 min-h-[calc(100vh-64px)] bg-white border-r border-slate-200 p-4" data-testid="admin-sidebar">
          <nav className="space-y-1">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => { setTab(t.id); setSearchTerm(""); setSortConfig({ key: null, dir: "asc" }); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-[#19798a] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`admin-tab-${t.id}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* ============ OVERVIEW TAB ============ */}
          {tab === "overview" && stats && (
            <div data-testid="admin-overview">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Dashboard Overview</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Total Users" value={stats.total_users} icon={Users} color="bg-teal-50 text-teal-600" />
                <StatCard label="Transactions" value={stats.total_transactions} icon={Activity} color="bg-green-50 text-green-600" />
                <StatCard label="Volume" value={`$${stats.total_transaction_volume.toLocaleString("en-US", {minimumFractionDigits: 2})}`} icon={DollarSign} color="bg-amber-50 text-amber-600" />
                <StatCard label="Wallet Balances" value={`$${stats.total_wallet_balance.toLocaleString("en-US", {minimumFractionDigits: 2})}`} icon={Wallet} color="bg-purple-50 text-purple-600" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <StatCard label="Referrals" value={stats.total_referrals} icon={Gift} color="bg-pink-50 text-pink-600" />
                <StatCard label="Saved Contacts" value={stats.total_contacts} icon={BookUser} color="bg-cyan-50 text-cyan-600" />
                <StatCard label="Pending Claims" value={stats.pending_claims} icon={Clock} color="bg-orange-50 text-orange-600" />
              </div>
              {stats.transaction_breakdown && Object.keys(stats.transaction_breakdown).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Transaction Breakdown</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.transaction_breakdown).map(([type, data]) => (
                      <div key={type} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <span className="text-sm font-medium text-slate-700 capitalize">{type.replace("_", " ")}</span>
                        <div className="flex items-center gap-6">
                          <span className="text-sm text-slate-500">{data.count} txn{data.count !== 1 ? "s" : ""}</span>
                          <span className="text-sm font-bold text-slate-900">${data.volume.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============ USERS TAB (UPDATED - Item 7: KYC Status column) ============ */}
          {tab === "users" && (
            <div data-testid="admin-users">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">All Users ({users.length})</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search users..." className="h-10 pl-10 pr-4 rounded-lg border border-slate-200 bg-white focus:border-[#19798a] outline-none text-sm w-64" data-testid="admin-users-search" />
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-users-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <SortHeader label="User" sortKey="name" className="text-left" />
                      <SortHeader label="Phone" sortKey="phone" className="text-left" />
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">KYC Status</th>
                      <SortHeader label="Bank" sortKey="bank_name" className="text-left" />
                      <SortHeader label="Balance" sortKey="wallet_balance" className="text-right" />
                      <SortHeader label="Referral Earnings" sortKey="referral_earnings" className="text-right" />
                      <SortHeader label="Joined" sortKey="created_at" className="text-left" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortData(filteredUsers, sortConfig.key, sortConfig.dir).map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#19798a] rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{u.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{u.name}</p>
                              <p className="text-xs text-slate-400">{u.email || "—"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 mono">{u.phone}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => { setTab("compliance"); setSearchTerm(""); }} className="cursor-pointer" data-testid={`kyc-badge-${u.phone}`}>
                            {statusBadge(kycMap[u.phone] || "Pending")}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          {u.is_banked ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">{u.bank_name}</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Unbanked</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">${u.wallet_balance?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-sm text-green-600 font-medium">${u.referral_earnings?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {/* ============ TRANSACTIONS TAB (UPDATED - Item 6: Flag Status + Dispute columns) ============ */}
          {tab === "transactions" && (
            <div data-testid="admin-transactions">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">All Transactions ({transactions.length})</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search transactions..." className="h-10 pl-10 pr-4 rounded-lg border border-slate-200 bg-white focus:border-[#19798a] outline-none text-sm w-64" data-testid="admin-tx-search" />
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-transactions-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <SortHeader label="Type" sortKey="transaction_type" className="text-left" />
                      <SortHeader label="From" sortKey="sender_name" className="text-left" />
                      <SortHeader label="To" sortKey="recipient_name" className="text-left" />
                      <SortHeader label="Amount" sortKey="amount" className="text-right" />
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Flag Status</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Dispute</th>
                      <SortHeader label="Reference" sortKey="fednow_reference" className="text-left" />
                      <SortHeader label="Date" sortKey="created_at" className="text-left" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortData(filteredTx, sortConfig.key, sortConfig.dir).map((tx) => (
                      <tr key={tx.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            tx.transaction_type === "p2p" ? "bg-teal-50 text-teal-700" :
                            tx.transaction_type === "atm_withdrawal" ? "bg-orange-50 text-orange-700" :
                            tx.transaction_type === "qr_payment" ? "bg-purple-50 text-purple-700" :
                            "bg-slate-50 text-slate-700"
                          }`}>
                            {tx.transaction_type === "p2p" && <ArrowRightLeft className="w-3 h-3" />}
                            {tx.transaction_type === "atm_withdrawal" && <Banknote className="w-3 h-3" />}
                            {tx.transaction_type === "qr_payment" && <QrCode className="w-3 h-3" />}
                            {tx.transaction_type?.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-900">{tx.sender_name}</p>
                          <p className="text-xs text-slate-400 mono">{tx.sender_phone}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-900">{tx.recipient_name || "Pending"}</p>
                          <p className="text-xs text-slate-400 mono">{tx.recipient_phone}</p>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">${tx.amount?.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            tx.status === "completed" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                          }`}>
                            {tx.status}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`flag-${tx.id}`}>
                          {statusBadge(getFlagStatus(tx.amount))}
                        </td>
                        <td className="px-4 py-3">
                          {disputeTxIds.has(tx.fednow_reference) ? (
                            <button onClick={() => { setTab("disputes"); }} className="text-xs font-medium text-amber-600 hover:underline" data-testid={`view-dispute-${tx.id}`}>View Dispute</button>
                          ) : (
                            <button className="text-xs font-medium text-[#19798a] hover:underline" data-testid={`file-dispute-${tx.id}`}>File Dispute</button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 mono">{tx.fednow_reference}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{tx.created_at ? new Date(tx.created_at).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {filteredTx.length === 0 && (
                  <div className="text-center py-12 text-slate-400">No transactions found</div>
                )}
              </div>
            </div>
          )}

          {/* ============ REFERRALS TAB ============ */}
          {tab === "referrals" && (
            <div data-testid="admin-referrals">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Referral Rewards ({referrals.length})</h2>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full" data-testid="admin-referrals-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Referrer</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">New User</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Referrer Bonus</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">New User Bonus</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {referrals.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-700 mono">{r.referrer_phone}</td>
                        <td className="px-4 py-3 text-sm text-slate-700 mono">{r.referee_phone}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-green-600">${r.referrer_bonus?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-green-600">${r.referee_bonus?.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">{r.status}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {referrals.length === 0 && (
                  <div className="text-center py-12 text-slate-400">No referrals yet</div>
                )}
              </div>
            </div>
          )}

          {/* ============ COMPLIANCE TAB (Item 1) ============ */}
          {tab === "compliance" && (
            <div data-testid="admin-compliance">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Compliance</h2>

              {/* A. BSA Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="bsa-summary">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3"><span className="text-sm text-slate-500">CTRs This Month</span><div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600"><FileText className="w-4 h-4" /></div></div>
                  <p className="text-2xl font-bold text-slate-900">2</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3"><span className="text-sm text-slate-500">SARs Filed</span><div className="w-9 h-9 rounded-lg flex items-center justify-center bg-green-50 text-green-600"><Shield className="w-4 h-4" /></div></div>
                  <p className="text-2xl font-bold text-slate-900">0</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3"><span className="text-sm text-slate-500">Pending Reviews</span><div className="w-9 h-9 rounded-lg flex items-center justify-center bg-orange-50 text-orange-600"><Clock className="w-4 h-4" /></div></div>
                  <p className="text-2xl font-bold text-slate-900">3</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-3"><span className="text-sm text-slate-500">OFAC Matches</span><div className="w-9 h-9 rounded-lg flex items-center justify-center bg-green-50 text-green-600"><ShieldCheck className="w-4 h-4" /></div></div>
                  <p className="text-2xl font-bold text-slate-900">0</p>
                </div>
              </div>

              {/* B. KYC Status Table */}
              <div className="bg-white rounded-xl border border-slate-200 mb-8" data-testid="kyc-table">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-900">KYC Status</h3>
                  <div className="flex items-center gap-2">
                    <div className="relative mr-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-white focus:border-[#19798a] outline-none text-sm w-48" data-testid="compliance-kyc-search" />
                    </div>
                    {["all","Verified","Pending","Failed"].map(f => (
                      <button key={f} onClick={() => setComplianceFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${complianceFilter === f ? "bg-[#19798a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`kyc-filter-${f.toLowerCase()}`}>
                        {f === "all" ? "All" : f}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Name</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Phone</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">KYC Status</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">ID Type</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Submitted</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Reviewed</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredKyc.filter(k => !searchTerm || k.name.toLowerCase().includes(searchTerm.toLowerCase()) || k.phone.includes(searchTerm)).map((k, i) => (
                      <tr key={k.phone} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{k.name}</td>
                        <td className="px-4 py-3 text-sm text-slate-700 mono">{k.phone}</td>
                        <td className="px-4 py-3">{statusBadge(k.status)}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{k.idType}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{k.submitted}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{k.reviewed || "—"}</td>
                        <td className="px-4 py-3">
                          <button className="text-xs font-medium text-[#19798a] hover:underline" data-testid={`kyc-action-${i}`}>{k.status === "Verified" ? "View" : "Review"}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>

              {/* C. Transaction Monitoring */}
              <div className="bg-white rounded-xl border border-slate-200 mb-8" data-testid="flagged-transactions">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="text-lg font-bold text-slate-900">Flagged Transactions</h3>
                </div>
                {flaggedTx.some(t => t.flag === "CTR Required") && (
                  <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="ctr-warning-banner">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-700 font-medium">1 transaction requires a CTR filing. Federal law requires CTR filing within 15 days of the transaction date.</p>
                  </div>
                )}
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Transaction ID</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">User</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Amount</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Type</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Date</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Flag</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {flaggedTx.map((ft, i) => (
                      <tr key={ft.txId} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm mono text-slate-700">{ft.txId}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{ft.user}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">${ft.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{ft.type}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{ft.date}</td>
                        <td className="px-4 py-3">{statusBadge(ft.flag)}</td>
                        <td className="px-4 py-3">
                          <button className="text-xs font-medium text-[#19798a] hover:underline" data-testid={`flagged-action-${i}`}>{ft.flag === "CTR Required" ? "File CTR" : "Investigate"}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>

              {/* D. OFAC Watchlist Screening */}
              <div className="bg-white rounded-xl border border-slate-200" data-testid="ofac-screening">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="text-lg font-bold text-slate-900">OFAC Watchlist Screening</h3>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">User</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Screening Date</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Last Checked</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ofacData.map((o, i) => (
                      <tr key={o.user} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{o.user}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{o.date}</td>
                        <td className="px-4 py-3">{statusBadge(o.status)}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{o.date}</td>
                        <td className="px-4 py-3">
                          <button className="text-xs font-medium text-[#19798a] hover:underline" data-testid={`ofac-action-${i}`}>{o.status === "Pending" ? "Check Now" : "Re-Screen"}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {/* ============ DISPUTES TAB (Item 2) ============ */}
          {tab === "disputes" && (
            <div data-testid="admin-disputes" className="flex gap-6">
              <div className={`${disputeDetail ? "flex-1" : "w-full"}`}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Disputes ({disputes.length})</h2>
                  <div className="flex items-center gap-2">
                    <div className="relative mr-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search disputes..." className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-white focus:border-[#19798a] outline-none text-sm w-48" data-testid="disputes-search" />
                    </div>
                    {["all","open","under-review","resolved","denied"].map(f => (
                      <button key={f} onClick={() => setDisputeFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${disputeFilter === f ? "bg-[#19798a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`dispute-filter-${f}`}>
                        {f === "all" ? "All" : f === "under-review" ? "Under Review" : f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full" data-testid="disputes-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Dispute ID</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">User</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Transaction ID</th>
                        <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Amount</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Type</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Date Filed</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Status</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredDisputes.filter(d => !searchTerm || d.user.toLowerCase().includes(searchTerm.toLowerCase()) || d.id.toLowerCase().includes(searchTerm.toLowerCase())).map((d) => (
                        <tr key={d.id} className={`hover:bg-slate-50 ${disputeDetail?.id === d.id ? "bg-teal-50/50" : ""}`}>
                          <td className="px-4 py-3 text-sm mono font-medium text-slate-700">{d.id}</td>
                          <td className="px-4 py-3 text-sm text-slate-900">{d.user}</td>
                          <td className="px-4 py-3 text-sm mono text-slate-500">{d.txId}</td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">${d.amount.toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{d.type}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{d.dateFiled}</td>
                          <td className="px-4 py-3">{statusBadge(d.status)}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => setDisputeDetail(d)} className="text-xs font-medium text-[#19798a] hover:underline" data-testid={`dispute-action-${d.id}`}>
                              {d.status === "Resolved" || d.status === "Denied" ? "View" : "Review"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-500">All disputes are processed in accordance with Federal Reserve Regulation E. Provisional credit must be issued within 10 business days of dispute receipt. Investigation must be completed within 45 days.</p>
                </div>
              </div>

              {/* Dispute Detail Panel */}
              {disputeDetail && (
                <div className="w-96 bg-white rounded-xl border border-slate-200 p-6 h-fit sticky top-6" data-testid="dispute-detail-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-slate-900">Dispute Detail</h3>
                    <button onClick={() => setDisputeDetail(null)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Dispute ID</span><span className="font-medium text-slate-900">{disputeDetail.id}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Date Filed</span><span className="text-slate-700">{disputeDetail.dateFiled}</span></div>
                    <hr className="border-slate-100" />
                    <div className="flex justify-between text-sm"><span className="text-slate-500">User</span><span className="font-medium text-slate-900">{disputeDetail.user}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Phone</span><span className="mono text-slate-700">{disputeDetail.phone}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">KYC Status</span>{statusBadge(disputeDetail.kycStatus)}</div>
                    <hr className="border-slate-100" />
                    <p className="text-xs font-medium text-slate-500 uppercase">Original Transaction</p>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Amount</span><span className="font-bold text-slate-900">${disputeDetail.amount.toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span className="text-slate-700">{disputeDetail.txDate}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Type</span><span className="text-slate-700">{disputeDetail.txType}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Memo</span><span className="text-slate-700">{disputeDetail.memo}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-500">Rail</span><span className="text-slate-700">{disputeDetail.rail}</span></div>
                    <hr className="border-slate-100" />
                    <p className="text-xs font-medium text-slate-500 uppercase">User Statement</p>
                    <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{disputeDetail.statement}</p>
                    <div className="space-y-2 pt-2">
                      <button className="w-full py-2.5 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-colors" data-testid="dispute-resolve-btn">Resolve in User's Favor</button>
                      <button className="w-full py-2.5 rounded-lg text-sm font-medium bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors" data-testid="dispute-deny-btn">Deny Dispute</button>
                      <button className="w-full py-2.5 rounded-lg text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors" data-testid="dispute-escalate-btn">Escalate to Compliance</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============ AUDIT LOG TAB (Item 3) ============ */}
          {tab === "audit" && (
            <div data-testid="admin-audit">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Audit Log</h2>
                  <p className="text-sm text-slate-500">90-day activity retention</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search logs..." className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-white focus:border-[#19798a] outline-none text-sm w-48" data-testid="audit-search" />
                  </div>
                  <div className="flex items-center gap-1">
                    {["7","30","90"].map(d => (
                      <button key={d} onClick={() => setAuditRange(d)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${auditRange === d ? "bg-[#19798a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`audit-range-${d}`}>
                        {d} Days
                      </button>
                    ))}
                  </div>
                  <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-[#19798a] text-white rounded-lg text-sm font-medium hover:bg-[#0d5c6b] transition-colors" data-testid="export-csv-btn">
                    <Download className="w-4 h-4" />Export to CSV
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full" data-testid="audit-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <SortHeader label="Timestamp" sortKey="ts" className="text-left" />
                      <SortHeader label="Admin User" sortKey="admin" className="text-left" />
                      <SortHeader label="Action" sortKey="action" className="text-left" />
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Details</th>
                      <SortHeader label="IP Address" sortKey="ip" className="text-left" />
                      <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortData(auditLog.filter(l => !searchTerm || l.action.toLowerCase().includes(searchTerm.toLowerCase()) || l.details.toLowerCase().includes(searchTerm.toLowerCase())), sortConfig.key, sortConfig.dir).map((l, i) => (
                      <tr key={`audit-${l.ts}-${l.action}-${i}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{l.ts}</td>
                        <td className="px-4 py-3 text-sm text-slate-700 mono">{l.admin}</td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{l.action}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{l.details}</td>
                        <td className="px-4 py-3 text-sm text-slate-500 mono">{l.ip}</td>
                        <td className="px-4 py-3">{statusBadge(l.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {/* ============ SETTINGS TAB (Item 4) ============ */}
          {tab === "settings" && (
            <div data-testid="admin-settings" className="max-w-4xl">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Settings</h2>

              {/* A. Transaction Limits */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="settings-limits">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Transaction Limits</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Daily P2P Send Limit</label><div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="number" value={settingsLimits.dailySend} onChange={(e) => setSettingsLimits(p => ({...p, dailySend: e.target.value}))} className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="limit-daily-send" /></div></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Single Transaction Maximum</label><div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="number" value={settingsLimits.singleMax} onChange={(e) => setSettingsLimits(p => ({...p, singleMax: e.target.value}))} className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="limit-single-max" /></div></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Daily ATM Withdrawal Limit</label><div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="number" value={settingsLimits.dailyATM} onChange={(e) => setSettingsLimits(p => ({...p, dailyATM: e.target.value}))} className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="limit-daily-atm" /></div></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Monthly Wallet Funding Limit</label><div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="number" value={settingsLimits.monthlyFunding} onChange={(e) => setSettingsLimits(p => ({...p, monthlyFunding: e.target.value}))} className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="limit-monthly-funding" /></div></div>
                </div>
                <button className="mt-4 px-6 py-2.5 bg-[#19798a] text-white rounded-lg text-sm font-medium hover:bg-[#0d5c6b] transition-colors" data-testid="save-limits-btn">Save Changes</button>
              </div>

              {/* B. Referral Program */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="settings-referral">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Referral Program</h3>
                <ToggleSwitch checked={settingsReferral.active} onChange={(v) => setSettingsReferral(p => ({...p, active: v}))} label="Referral Program Active" sublabel={settingsReferral.active ? "Active" : "Off"} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Referrer Bonus Amount</label><div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="number" value={settingsReferral.referrerBonus} onChange={(e) => setSettingsReferral(p => ({...p, referrerBonus: e.target.value}))} className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="referral-referrer-bonus" /></div></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">New User Welcome Bonus</label><div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="number" value={settingsReferral.newUserBonus} onChange={(e) => setSettingsReferral(p => ({...p, newUserBonus: e.target.value}))} className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="referral-new-user-bonus" /></div></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Max Referrals/Month</label><input type="number" value={settingsReferral.maxPerMonth} onChange={(e) => setSettingsReferral(p => ({...p, maxPerMonth: e.target.value}))} className="w-full h-10 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="referral-max-per-month" /></div>
                </div>
                <button className="mt-4 px-6 py-2.5 bg-[#19798a] text-white rounded-lg text-sm font-medium hover:bg-[#0d5c6b] transition-colors" data-testid="save-referral-btn">Save Changes</button>
              </div>

              {/* C. White-Label Branding */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="settings-branding">
                <h3 className="text-lg font-bold text-slate-900 mb-4">White-Label Branding</h3>
                <div className="flex gap-6">
                  <div className="flex-1 space-y-4">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Bank Name</label><input type="text" value={settingsBranding.bankName} onChange={(e) => setSettingsBranding(p => ({...p, bankName: e.target.value}))} className="w-full h-10 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="branding-bank-name" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">App Subtitle</label><input type="text" value={settingsBranding.subtitle} onChange={(e) => setSettingsBranding(p => ({...p, subtitle: e.target.value}))} className="w-full h-10 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm" data-testid="branding-subtitle" /></div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Primary Color</label>
                      <div className="flex items-center gap-3">
                        <input type="text" value={settingsBranding.primaryColor} onChange={(e) => setSettingsBranding(p => ({...p, primaryColor: e.target.value}))} className="flex-1 h-10 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] outline-none text-sm mono" data-testid="branding-primary-color" />
                        <div className="w-10 h-10 rounded-lg border border-slate-200" style={{ backgroundColor: settingsBranding.primaryColor }} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Logo Upload</label>
                      <button className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-[#19798a] hover:text-[#19798a] transition-colors w-full justify-center" data-testid="branding-upload-logo">
                        <Upload className="w-4 h-4" />Upload Bank Logo (PNG, SVG)
                      </button>
                    </div>
                  </div>
                  {/* Mini Preview */}
                  <div className="w-56 flex-shrink-0">
                    <p className="text-xs font-medium text-slate-500 mb-2">Preview</p>
                    <div className="rounded-lg border border-slate-200 p-4" style={{ backgroundColor: settingsBranding.primaryColor }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                          <BankLogoIcon size={32} />
                        </div>
                        <div>
                          <p className="text-white text-sm font-bold">{settingsBranding.bankName.replace("C.U.", "Pay")}</p>
                          <p className="text-white/50 text-[8px]">{settingsBranding.subtitle}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button className="mt-4 px-6 py-2.5 bg-[#19798a] text-white rounded-lg text-sm font-medium hover:bg-[#0d5c6b] transition-colors" data-testid="save-branding-btn">Save Branding</button>
              </div>

              {/* D. Notification Settings */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="settings-notifications">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Notification Settings</h3>
                <div className="divide-y divide-slate-100">
                  <ToggleSwitch checked={settingsNotifs.newUser} onChange={(v) => setSettingsNotifs(p => ({...p, newUser: v}))} label="New User Registration" />
                  <ToggleSwitch checked={settingsNotifs.txOver1k} onChange={(v) => setSettingsNotifs(p => ({...p, txOver1k: v}))} label="Transaction Over $1,000" />
                  <ToggleSwitch checked={settingsNotifs.failedKyc} onChange={(v) => setSettingsNotifs(p => ({...p, failedKyc: v}))} label="Failed KYC Verification" />
                  <ToggleSwitch checked={settingsNotifs.newDispute} onChange={(v) => setSettingsNotifs(p => ({...p, newDispute: v}))} label="New Dispute Filed" />
                  <ToggleSwitch checked={settingsNotifs.ctrReached} onChange={(v) => setSettingsNotifs(p => ({...p, ctrReached: v}))} label="CTR Threshold Reached" />
                  <ToggleSwitch checked={settingsNotifs.ofacMatch} onChange={(v) => setSettingsNotifs(p => ({...p, ofacMatch: v}))} label="OFAC Match Detected" />
                  <ToggleSwitch checked={settingsNotifs.newDevice} onChange={(v) => setSettingsNotifs(p => ({...p, newDevice: v}))} label="Admin Login from New Device" />
                </div>
                <button className="mt-4 px-6 py-2.5 bg-[#19798a] text-white rounded-lg text-sm font-medium hover:bg-[#0d5c6b] transition-colors" data-testid="save-notifications-btn">Save Preferences</button>
              </div>
            </div>
          )}

          {/* ============ REPORTS TAB (Item 5) ============ */}
          {tab === "reports" && (
            <div data-testid="admin-reports">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Reports & Analytics</h2>
                <div className="flex items-center gap-3">
                  {["7","30","90"].map(d => (
                    <button key={d} onClick={() => setReportRange(d)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${reportRange === d ? "bg-[#19798a] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`report-range-${d}`}>
                      {d} Days
                    </button>
                  ))}
                  <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid="report-range-custom">Custom</button>
                  <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-[#19798a] text-white rounded-lg text-sm font-medium hover:bg-[#0d5c6b] transition-colors" data-testid="export-pdf-btn">
                    <Download className="w-4 h-4" />Export PDF Report
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* A. Monthly Transaction Volume - Bar Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="chart-volume">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Transaction Volume by Week</h3>
                  <p className="text-sm text-slate-500 mb-6">Past 5 weeks</p>
                  <div className="flex items-end gap-3 h-48">
                    {[
                      { label: "Mar 3-9", value: 4200 },
                      { label: "Mar 10-16", value: 6800 },
                      { label: "Mar 17-23", value: 9100 },
                      { label: "Mar 24-30", value: 7650 },
                      { label: "Apr 1-2", value: 3200 },
                    ].map((bar) => (
                      <div key={bar.label} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs font-medium text-slate-700">${(bar.value / 1000).toFixed(1)}k</span>
                        <div className="w-full bg-[#19798a] rounded-t-md transition-all" style={{ height: `${(bar.value / 9100) * 140}px` }} />
                        <span className="text-[10px] text-slate-500 text-center leading-tight mt-1">{bar.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* B. Transaction Type Breakdown - Pie Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="chart-breakdown">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Transaction Type Distribution</h3>
                  <p className="text-sm text-slate-500 mb-6">47 transactions total</p>
                  <div className="flex items-center gap-8">
                    <div className="w-40 h-40 rounded-full flex-shrink-0" style={{ background: "conic-gradient(#19798a 0% 45%, #f48420 45% 73%, #60a5fa 73% 90%, #94a3b8 90% 100%)" }}>
                      <div className="w-full h-full rounded-full flex items-center justify-center">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                          <span className="text-lg font-bold text-slate-900">47</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 flex-1">
                      {[
                        { label: "P2P Transfers", pct: "45%", color: "bg-[#19798a]" },
                        { label: "QR Payments", pct: "28%", color: "bg-[#f48420]" },
                        { label: "ATM Withdrawals", pct: "17%", color: "bg-blue-400" },
                        { label: "Wallet Funding", pct: "10%", color: "bg-slate-400" },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${item.color}`} />
                          <span className="text-sm text-slate-700 flex-1">{item.label}</span>
                          <span className="text-sm font-bold text-slate-900">{item.pct}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* C. User Growth - Line Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="chart-user-growth">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">New User Registrations</h3>
                  <p className="text-sm text-slate-500 mb-6">5 total users</p>
                  <div className="h-40 relative">
                    <svg viewBox="0 0 500 160" className="w-full h-full">
                      {/* Grid lines */}
                      {[0,1,2].map(i => <line key={`grid-${i}`} x1="40" y1={20 + i * 60} x2="480" y2={20 + i * 60} stroke="#e2e8f0" strokeWidth="1" />)}
                      {/* Y-axis labels */}
                      <text x="30" y="25" textAnchor="end" fill="#94a3b8" fontSize="11">2</text>
                      <text x="30" y="85" textAnchor="end" fill="#94a3b8" fontSize="11">1</text>
                      <text x="30" y="145" textAnchor="end" fill="#94a3b8" fontSize="11">0</text>
                      {/* Line */}
                      <polyline fill="none" stroke="#19798a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                        points="90,80 200,80 310,20 420,80 480,140" />
                      {/* Dots */}
                      {[[90,80],[200,80],[310,20],[420,80],[480,140]].map(([x,y], i) => (
                        <circle key={`dot-${x}-${y}`} cx={x} cy={y} r="5" fill="#19798a" stroke="white" strokeWidth="2" />
                      ))}
                      {/* X-axis labels */}
                      <text x="90" y="158" textAnchor="middle" fill="#94a3b8" fontSize="10">Wk 1</text>
                      <text x="200" y="158" textAnchor="middle" fill="#94a3b8" fontSize="10">Wk 2</text>
                      <text x="310" y="158" textAnchor="middle" fill="#94a3b8" fontSize="10">Wk 3</text>
                      <text x="420" y="158" textAnchor="middle" fill="#94a3b8" fontSize="10">Wk 4</text>
                      <text x="480" y="158" textAnchor="middle" fill="#94a3b8" fontSize="10">Wk 5</text>
                    </svg>
                  </div>
                </div>
              </div>

              {/* D. Summary Stats Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="report-summary">
                <StatCard label="Total Volume (30d)" value="$30,950.00" icon={DollarSign} color="bg-teal-50 text-teal-600" />
                <StatCard label="Total Transactions" value="47" icon={Activity} color="bg-green-50 text-green-600" />
                <StatCard label="Active Users" value="5" icon={Users} color="bg-purple-50 text-purple-600" />
                <StatCard label="Avg Transaction Size" value="$658.51" icon={BarChart3} color="bg-amber-50 text-amber-600" />
              </div>
            </div>
          )}
        </main>
      </div>
      <AdminFooter />
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
  </div>
);

// Transaction Receipt Modal
const TransactionReceipt = ({ transaction, onClose }) => {
  if (!transaction) return null;
  const isSent = transaction.type === "sent";
  
  const handleShare = () => {
    const text = `Transaction Receipt\nID: ${transaction.id}\nType: ${transaction.transaction_type}\nAmount: $${transaction.amount.toFixed(2)}\nFrom: ${transaction.sender_name || "—"}\nTo: ${transaction.counterparty_name}\nDate: ${new Date(transaction.created_at).toLocaleString()}\nRef: ${transaction.fednow_reference || "—"}`;
    if (navigator.share) {
      navigator.share({ title: "Transaction Receipt", text });
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up" onClick={(e) => e.stopPropagation()} data-testid="transaction-receipt">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-900">Transaction Receipt</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-center mb-6">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${isSent ? "bg-red-50" : "bg-green-50"}`}>
            <CheckCircle2 className={`w-7 h-7 ${isSent ? "text-red-500" : "text-green-500"}`} />
          </div>
          <p className={`text-3xl font-bold ${isSent ? "text-red-600" : "text-green-600"}`}>{isSent ? "-" : "+"}${transaction.amount.toFixed(2)}</p>
          <p className="text-sm text-slate-500 mt-1 capitalize">{transaction.transaction_type?.replace("_", " ")}</p>
        </div>
        <div className="space-y-3 bg-slate-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Transaction ID</span><span className="mono text-slate-700 text-xs">{transaction.id?.slice(0, 12)}...</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Type</span><span className="text-slate-700 capitalize">{transaction.transaction_type?.replace("_", " ")}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">{isSent ? "Sent to" : "Received from"}</span><span className="text-slate-700">{transaction.counterparty_name}</span></div>
          {transaction.memo && <div className="flex justify-between text-sm"><span className="text-slate-500">Memo</span><span className="text-slate-700">{transaction.memo}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-slate-500">Rail</span><span className="text-slate-700">{transaction.transaction_type === "p2p" ? "FedNow Instant" : "Internal"}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="mono text-slate-700 text-xs">{transaction.fednow_reference || "—"}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Status</span><span className={`font-medium ${transaction.status === "completed" ? "text-green-600" : "text-amber-600"}`}>{transaction.status}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span className="text-slate-700">{new Date(transaction.created_at).toLocaleString()}</span></div>
        </div>
        <button onClick={handleShare} className="w-full btn-brand flex items-center justify-center gap-2" data-testid="share-receipt-btn">
          <Share2 className="w-4 h-4" />Share Receipt
        </button>
      </div>
    </div>
  );
};

// Add Money Page
const AddMoneyPage = () => {
  const navigate = useNavigate();
  const [option, setOption] = useState(null);
  const [bankName, setBankName] = useState("");
  const [routingNum, setRoutingNum] = useState("");
  const [accountNum, setAccountNum] = useState("");
  const [amount, setAmount] = useState("");
  const [success, setSuccess] = useState(false);

  const handleLink = () => {
    if (bankName && routingNum && accountNum && amount) setSuccess(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-lg">
        <button onClick={() => option ? setOption(null) : navigate("/dashboard")} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
          <ChevronRight className="w-4 h-4 rotate-180" />Back {option ? "to options" : "to Dashboard"}
        </button>

        {success ? (
          <div className="card-surface p-6 text-center animate-slide-up" data-testid="add-money-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Transfer Initiated</h2>
            <p className="text-slate-500 mb-2">${parseFloat(amount).toFixed(2)} from {bankName}</p>
            <p className="text-sm text-slate-400 mb-6">Funds will be available in 1-3 business days via ACH.</p>
            <button onClick={() => navigate("/dashboard")} className="w-full btn-brand" data-testid="add-money-done-btn">Done</button>
          </div>
        ) : !option ? (
          <div className="animate-slide-up">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Add Money</h2>
            <p className="text-slate-500 mb-6">Choose how you'd like to fund your wallet</p>
            <div className="space-y-3">
              <button onClick={() => setOption("ach")} className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-[#19798a]/30 hover:shadow-sm transition-all text-left" data-testid="add-money-ach">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center"><Building2 className="w-6 h-6 text-[#19798a]" /></div>
                <div className="flex-1"><p className="font-medium text-slate-900">Link Bank Account (ACH)</p><p className="text-sm text-slate-500">Connect your bank for direct transfers</p></div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
              <button onClick={() => setOption("instant")} className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-[#19798a]/30 hover:shadow-sm transition-all text-left" data-testid="add-money-instant">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center"><Zap className="w-6 h-6 text-amber-600" /></div>
                <div className="flex-1"><p className="font-medium text-slate-900">Instant Transfer</p><p className="text-sm text-slate-500">Fund instantly from debit card</p></div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
              <button onClick={() => setOption("check")} className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-[#19798a]/30 hover:shadow-sm transition-all text-left" data-testid="add-money-check">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center"><Camera className="w-6 h-6 text-green-600" /></div>
                <div className="flex-1"><p className="font-medium text-slate-900">Check Deposit</p><p className="text-sm text-slate-500">Deposit a check using your camera</p></div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>
        ) : option === "ach" ? (
          <div className="card-surface p-6 animate-slide-up" data-testid="ach-form">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link Bank Account</h2>
            <p className="text-slate-500 mb-6">Enter your bank details for ACH transfer</p>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Bank Name</label><input type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none" placeholder="e.g. Chase, Wells Fargo" data-testid="ach-bank-name" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Routing Number</label><input type="text" value={routingNum} onChange={(e) => setRoutingNum(e.target.value)} className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none mono" placeholder="9 digits" maxLength={9} data-testid="ach-routing" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Account Number</label><input type="text" value={accountNum} onChange={(e) => setAccountNum(e.target.value)} className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none mono" placeholder="Account number" data-testid="ach-account" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-12 pl-10 pr-4 rounded-lg border border-slate-200 bg-slate-50/50 focus:border-[#19798a] focus:ring-2 focus:ring-[#19798a]/20 outline-none" placeholder="0.00" data-testid="ach-amount" /></div>
              </div>
            </div>
            <button onClick={handleLink} disabled={!bankName || !routingNum || !accountNum || !amount} className="w-full btn-brand mt-6" data-testid="ach-submit-btn">Initiate Transfer</button>
          </div>
        ) : option === "instant" ? (
          <div className="card-surface p-6 animate-slide-up text-center" data-testid="instant-transfer">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4"><Zap className="w-8 h-8 text-amber-600" /></div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Instant Transfer</h2>
            <p className="text-slate-500 mb-6">Link a debit card for instant funding. Funds available immediately.</p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6"><p className="text-sm text-slate-600">Debit card linking will be available soon. Use ACH for now.</p></div>
            <button onClick={() => setOption("ach")} className="w-full btn-brand">Use ACH Instead</button>
          </div>
        ) : (
          <div className="card-surface p-6 animate-slide-up text-center" data-testid="check-deposit">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4"><Camera className="w-8 h-8 text-green-600" /></div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check Deposit</h2>
            <p className="text-slate-500 mb-6">Take a photo of the front and back of your check.</p>
            <div className="space-y-3 mb-6">
              <button className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-[#19798a] hover:text-[#19798a] transition-colors" data-testid="check-front-btn"><Camera className="w-5 h-5" />Capture Front of Check</button>
              <button className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-[#19798a] hover:text-[#19798a] transition-colors" data-testid="check-back-btn"><Camera className="w-5 h-5" />Capture Back of Check</button>
            </div>
            <button disabled className="w-full btn-brand opacity-50">Submit for Review</button>
          </div>
        )}
      </main>
      <ConsumerFooter />
    </div>
  );
};

// Profile Page
const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const sections = [
    { label: "Linked Bank Accounts", icon: Building2, detail: "No accounts linked", action: () => navigate("/add-money") },
    { label: "Transaction Limits", icon: Shield, detail: "Daily Send: $5,000 | Daily ATM: $500" },
    { label: "Notification Settings", icon: Bell, detail: "Push & SMS enabled" },
    { label: "Security Settings", icon: Lock, detail: "Change PIN, Biometrics" },
    { label: "Help & Support", icon: HelpCircle, detail: "FAQ, Contact Support" },
    { label: "Terms of Service", icon: FileText, detail: "Legal agreements" },
    { label: "Privacy Policy", icon: Shield, detail: "How we handle your data" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <Header />
      <main className="container mx-auto px-4 md:px-8 py-8 max-w-lg">
        {/* Profile Header */}
        <div className="card-surface p-6 mb-6 text-center" data-testid="profile-header">
          <div className="w-20 h-20 bg-[#19798a] rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-white text-2xl font-bold">{user?.name?.charAt(0)?.toUpperCase()}</span>
          </div>
          <h2 className="text-xl font-bold text-slate-900">{user?.name}</h2>
          <p className="text-sm text-slate-500 mono">{user?.phone}</p>
          <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-green-50 rounded-full" data-testid="kyc-badge">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Identity Verified</span>
          </div>
        </div>

        {/* Settings List */}
        <div className="card-surface divide-y divide-slate-100" data-testid="profile-settings">
          {sections.map((s) => (
            <button key={s.label} onClick={s.action || (() => {})} className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 text-left transition-colors">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center"><s.icon className="w-5 h-5 text-slate-600" /></div>
              <div className="flex-1"><p className="text-sm font-medium text-slate-900">{s.label}</p><p className="text-xs text-slate-400">{s.detail}</p></div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </button>
          ))}
        </div>

        {/* Logout */}
        <button onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center justify-center gap-2 mt-6 py-3 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors" data-testid="profile-logout-btn">
          <LogOut className="w-5 h-5" />Sign Out
        </button>
      </main>
      <ConsumerFooter />
      <BottomNav active="profile" />
    </div>
  );
};

// Bottom Navigation Bar
const BottomNav = ({ active }) => {
  const navigate = useNavigate();
  const items = [
    { id: "home", icon: Home, label: "Home", path: "/dashboard" },
    { id: "send", icon: Send, label: "Send", path: "/send" },
    { id: "qr", icon: QrCode, label: "QR Pay", path: "/qr-pay" },
    { id: "profile", icon: UserCircle, label: "Profile", path: "/profile" },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 lg:hidden" data-testid="bottom-nav">
      <div className="flex items-center justify-around py-2">
        {items.map((item) => (
          <button key={item.id} onClick={() => navigate(item.path)} className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${active === item.id ? "text-[#19798a]" : "text-slate-400"}`} data-testid={`nav-${item.id}`}>
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/claim/:token" element={<ClaimPage />} />
              <Route path="/mobile" element={<MobileSimulatorPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/profile" element={
                <ProtectedRoute><ProfilePage /></ProtectedRoute>
              } />
              <Route path="/add-money" element={
                <ProtectedRoute><AddMoneyPage /></ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path="/send" element={
                <ProtectedRoute><SendMoneyPage /></ProtectedRoute>
              } />
              <Route path="/atm" element={
                <ProtectedRoute><ATMPage /></ProtectedRoute>
              } />
              <Route path="/qr-pay" element={
                <ProtectedRoute><QRPayPage /></ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute><HistoryPage /></ProtectedRoute>
              } />
              <Route path="/referrals" element={
                <ProtectedRoute><ReferralsPage /></ProtectedRoute>
              } />
              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
